import { useCreateOrUpdateArtworkInAuction } from "@/api/related-fields";
import { FormLayoutElement } from "@/config/forms/general";
import { ArtworkInAuction } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import LinksDrawerLayout from "./LinksDrawerLayout";

const ArtworkInAuctionDrawer = ({
  fields,
  data,
  queryId,
  type,
  open,
  onOpenChange,
}: {
  fields: FormLayoutElement[] | undefined;
  data: ArtworkInAuction;
  queryId: string;
  type: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const schema = z.object({
    oeuvresDansVentesDescriptionNumeroLot: z.string().optional(),
    oeuvresDansVentesDescriptionReproduitPage: z.string().optional(),
    oeuvresDansVentesDescriptionEstimationHaute: z.string().optional(),
    oeuvresDansVentesDescriptionEstimationBasse: z.string().optional(),
    oeuvresDansVentesDescriptionVendu: z.boolean().optional(),
    oeuvresDansVentesDescriptionResultatNonListe: z.boolean().optional(),
    oeuvresDansVentesDescriptionNonVenu: z.boolean().optional(),
    oeuvresDansVentesDescriptionNonVendu: z.boolean().optional(),
    oeuvresDansVentesDescriptionNonReproduit: z.boolean().optional(),
    oeuvresDansVentesDescriptionRetire: z.boolean().optional(),
    oeuvresDansVentesDescriptionMiseAPrix: z.string().optional(),
    oeuvresDansVentesDescriptionPrixMarteau: z.string().optional(),
    oeuvresDansVentesDescriptionPrixAvecFrais: z.string().optional(),
    oeuvresDansVentesDescriptionNotes: z.string().optional(),
    auction: z.object({
      ventesDescriptionNumeroVente: z.string().optional(),
      ventesDescriptionResume: z.string().optional(),
    }),
  });

  const { mutate: createOrUpdateArtworkInAuction } =
    useCreateOrUpdateArtworkInAuction(queryId, type);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      auction: data.auction,
      oeuvresDansVentesDescriptionNumeroLot:
        data.oeuvresDansVentesDescriptionNumeroLot,
      oeuvresDansVentesDescriptionReproduitPage:
        data.oeuvresDansVentesDescriptionReproduitPage,
      oeuvresDansVentesDescriptionEstimationHaute:
        data.oeuvresDansVentesDescriptionEstimationHaute,
      oeuvresDansVentesDescriptionEstimationBasse:
        data.oeuvresDansVentesDescriptionEstimationBasse,
      oeuvresDansVentesDescriptionVendu: data.oeuvresDansVentesDescriptionVendu,
      oeuvresDansVentesDescriptionResultatNonListe:
        data.oeuvresDansVentesDescriptionResultatNonListe,
      oeuvresDansVentesDescriptionNonVenu:
        data.oeuvresDansVentesDescriptionNonVenu,
      oeuvresDansVentesDescriptionNonVendu:
        data.oeuvresDansVentesDescriptionNonVendu,
      oeuvresDansVentesDescriptionNonReproduit:
        data.oeuvresDansVentesDescriptionNonReproduit,
      oeuvresDansVentesDescriptionRetire:
        data.oeuvresDansVentesDescriptionRetire,
      oeuvresDansVentesDescriptionMiseAPrix:
        data.oeuvresDansVentesDescriptionMiseAPrix,
      oeuvresDansVentesDescriptionPrixMarteau:
        data.oeuvresDansVentesDescriptionPrixMarteau,
      oeuvresDansVentesDescriptionPrixAvecFrais:
        data.oeuvresDansVentesDescriptionPrixAvecFrais,
      oeuvresDansVentesDescriptionNotes: data.oeuvresDansVentesDescriptionNotes,
    },
  });

  const onSubmit = (formData: z.infer<typeof schema>) => {
    createOrUpdateArtworkInAuction({
      id: data.id,
      artworkId: data.artwork.id,
      auctionId: data.auction.id,
      ...formData,
    });
  };
  const isFromAuction = type === "auction";
  const previewConfig = isFromAuction
    ? {
        attachments: data.artwork?.attachments ?? [],
        type: "artwork" as const,
        alt:
          data.artwork?.oeuvresDescriptionNumeroEnregistrement ??
          data.artwork?.oeuvresDescriptionTitre,
      }
    : {
        attachments: data.auction?.attachments ?? [],
        type: "auction" as const,
        alt:
          data.auction?.ventesDescriptionNumeroVente ??
          data.auction?.ventesDescriptionTitre,
      };

  return (
    <LinksDrawerLayout
      form={form}
      fields={fields ?? []}
      recordId={data.id}
      onSubmit={onSubmit}
      previewImage={previewConfig}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
};

export default ArtworkInAuctionDrawer;
